import { UseFormReturn } from 'react-hook-form';
import {
  Form,
  FormField,
  FormItem,
  FormMessage,
} from '@web/components/ui/form';

import { InputAnimatedLabel } from '@web/components/ui/input-animated-label';

import { BrandSelectData, DoctorData } from '@shared/types';

import { DoctorStatusDropdown } from '../common/doctor-status-dropdown';
import { DoctorBrandDropdown } from '../common/doctor-brand-dropdown';

type DoctorDetailsFormProps = {
  form: UseFormReturn<DoctorData>;
  onSubmit: (data: DoctorData) => Promise<void>;
  isEditMode: boolean;
  brandsList: BrandSelectData[];
  setIsSubmitting: (isSubmitting: boolean) => void;
};

export function DoctorDetailsForm({
  form,
  onSubmit,
  isEditMode,
  brandsList,
  setIsSubmitting,
}: DoctorDetailsFormProps) {
  return (
    <Form {...form}>
      <form
        onSubmit={(e) => {
          setIsSubmitting(true);

          void form.handleSubmit(onSubmit)(e);
        }}
      >
        <div
          className={`grid grid-cols-2 gap-4 transition-all duration-500 ease-in-out ${isEditMode ? 'scale-[1.02] transform' : ''}`}
        >
          {/* Full Name */}
          <FormField
            control={form.control}
            name="fullName"
            render={({ field }) => (
              <FormItem className="col-span-2">
                <InputAnimatedLabel
                  label="Full Name"
                  {...field}
                  readOnly={!isEditMode}
                />
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Display Name */}
          <FormField
            control={form.control}
            name="displayName"
            render={({ field }) => (
              <FormItem className="col-span-2">
                <InputAnimatedLabel
                  label="Display Name"
                  {...field}
                  readOnly={!isEditMode}
                />
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Web Page */}
          <FormField
            control={form.control}
            name="webpage"
            render={({ field }) => (
              <FormItem>
                <InputAnimatedLabel
                  label="Web Page"
                  {...field}
                  readOnly={!isEditMode}
                />
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Specialty */}
          <FormField
            control={form.control}
            name="specialty"
            render={({ field }) => (
              <FormItem>
                <InputAnimatedLabel
                  label="Speciality"
                  {...field}
                  readOnly={!isEditMode}
                />
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Email */}
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <InputAnimatedLabel
                  label="Email"
                  type="email"
                  {...field}
                  readOnly={!isEditMode}
                />
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Phone */}
          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <InputAnimatedLabel
                  label="Phone"
                  type="tel"
                  {...field}
                  readOnly={!isEditMode}
                />
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Status Dropdown */}
          <FormField
            control={form.control}
            name="status"
            render={({ field }) => (
              <FormItem className="col-span-2">
                <DoctorStatusDropdown
                  value={field.value}
                  readOnly={!isEditMode}
                  onSelect={(status) => {
                    form.setValue('status', status);
                  }}
                />
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Brand Dropdown */}
          <FormField
            control={form.control}
            name="brand"
            render={({ field }) => (
              <FormItem className="col-span-2">
                <DoctorBrandDropdown
                  brandsList={brandsList}
                  value={field.value}
                  readOnly={!isEditMode}
                  onSelect={(brand) => {
                    form.setValue('brand', brand);
                  }}
                />
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <button type="submit" className="hidden" />
      </form>
    </Form>
  );
}
